 import { ofType, StateObservable } from 'redux-observable';
import { ajax } from 'rxjs/ajax';
import { mergeMap, map, catchError, exhaustMap } from 'rxjs/operators';
 import { Observable, of } from 'rxjs';
import { APP_CONFIG } from "../../config/GeneralConfig";
import {
    eventNotificationError,
    eventNotificationShow,
    eventNotificationSuccess
} from "../common/EventNotificationActions";
import {EVENT_NOTIFICATION_TYPE} from "../../helpers/EventNotificationType";
import {
    IProjectLogEntry,
    ProjectLogModule,
    ProjectLogEntryRequest,
    ProjectLogEntryRequestProp, ProjectLogEntryId, ProjectLogId
} from '../../models/projects/ProjectLog';
import {ApiReturnType, FileRequestProp, getUserViewSettingsApiRequest} from "../../models/common/ApiReturnType";
 import {ErrorAction, ReduxAction} from "../../models/common/ActionTypes";
 import {AppState} from "../../reducers/RootReducer";
 import {
     getUrlParamsFromViewSettings,
    multipartHeaders,
    RouteConfig,
    RouteMethods,
    toFormDataEncode
} from "../../config/RouteConfig";
 import {sendUserToPage} from "../common/UserViewSettingsActions";
 import {Identifier, makeNumericId} from "../../models/common/Identifier";
 import {UserViewSettings, UserViewSettingsProp, ViewIdentifiable} from "../../models/common/ViewSettings";
 import { ProjectId } from "../../models/projects/Project";
 import {ProjectStageId} from "../../models/projects/ProjectStage";
 import {ProjectStagePhaseId} from "../../models/projects/ProjectStagePhase";
 import {Nullable} from "../../models/common/Nullable";
 import {fetchProjectById} from "./ProjectActions";

 type ProjectStagePhaseIdOptPath = {
     projectId: Nullable< ProjectId >
     stageId?: ProjectStageId
     phaseId?: ProjectStagePhaseId
 }

/**
 * typescript definitions
 */
export type ProjectLogActionsTypes = FetchProjectLogActionTypes
    | FetchProjectLogEntryThreadActionTypes
    | CreateProjectLogEntryActionTypes
    | MarkProjectLogEntryReadActionTypes

interface FetchProjectLogAction extends Partial< UserViewSettingsProp >, ReduxAction<typeof FETCH_PROJECT_LOG> {
    projectIdPath: ProjectStagePhaseIdOptPath
    attachmentsOnly: boolean
}
interface FetchProjectLogSuccessAction extends ReduxAction<typeof FETCH_PROJECT_LOG_SUCCESS> {
    response: ApiReturnType< IProjectLogEntry[] >
    projectIdPath: ProjectStagePhaseIdOptPath
}
interface FetchProjectLogErrorAction extends ErrorAction<typeof FETCH_PROJECT_LOG_ERROR>{
    projectIdPath: ProjectStagePhaseIdOptPath
}

type FetchProjectLogActionTypes = FetchProjectLogAction | FetchProjectLogSuccessAction | FetchProjectLogErrorAction

/**
 * redux action types for fetching project log entries.
 *
 * @type {string}
 */
export const FETCH_PROJECT_LOG = 'FETCH_PROJECT_LOG';
export const FETCH_PROJECT_LOG_SUCCESS = 'FETCH_PROJECT_LOG_SUCCESS';
export const FETCH_PROJECT_LOG_ERROR = 'FETCH_PROJECT_LOG_ERROR';

/**
 * initiates the fetching process of the log from the backend.
 * by default all projects will be included in the return. provide an projectId,
 * if you only want entries from that projects log
 */
export function fetchProjectLog(
    projectIdPath: ProjectStagePhaseIdOptPath,
    userViewSettings: UserViewSettings | undefined = undefined,
    attachmentsOnly: boolean = false
): FetchProjectLogAction {

    return { type: FETCH_PROJECT_LOG, projectIdPath, userViewSettings, attachmentsOnly };
}
/**
 * action will be called after the fetching request was successful and there was data returned from the backend
 *
 * @param { number | null } projectIdPath
 * @param { object } response see backend API doc for specific return shape
 * @returns {{type: string, response: object, projectId: number | null}}
 */
export function fetchProjectLogSuccess(
    projectIdPath: ProjectStagePhaseIdOptPath,
    response: ApiReturnType< IProjectLogEntry[] >
): FetchProjectLogSuccessAction {
    return { type: FETCH_PROJECT_LOG_SUCCESS, response, projectIdPath };
}
/**
 * action will be called, after the the request was NOT successful and there was an error returned from the backend
 *
 * @param { number | null } projectIdPath
 * @param {object} error
 * @returns {{type: string, error:  object, projectId: number| null}}
 */
export function fetchProjectLogError(
    projectIdPath: ProjectStagePhaseIdOptPath,
    error: any
): FetchProjectLogErrorAction {
    return { type: FETCH_PROJECT_LOG_ERROR, error, projectIdPath };
}

function getPathForFetchProjectLog( action: FetchProjectLogAction ): string {
    const { attachmentsOnly, projectIdPath } = action;
    let path = APP_CONFIG.baseWeb + "project";

    if( attachmentsOnly ) {
        if( typeof projectIdPath.phaseId !== "undefined" ) {
            path += `${ projectIdPath.projectId!.projectId.rawValue}/stages/${ projectIdPath.stageId!.projectStageId.rawValue }/phases/${projectIdPath.phaseId!.phaseId.rawValue}/log`;
        } else if( typeof projectIdPath.stageId !== "undefined" ) {
            path += `${ projectIdPath.projectId!.projectId.rawValue}/stages/${ projectIdPath.stageId!.projectStageId.rawValue }/log`;
        }
        else {
            path += `${ projectIdPath.projectId!.projectId.rawValue}/log`;
        }
    } else {

        if ( action.projectIdPath.projectId === null) {
            path += '/log/';
        } else if( typeof projectIdPath.phaseId !== "undefined" ) {
            path += `${ projectIdPath.projectId!.projectId.rawValue}/stages/${ projectIdPath.stageId!.projectStageId.rawValue }/phases/${projectIdPath.phaseId!.phaseId.rawValue}/attachments`;
        } else if( typeof projectIdPath.stageId !== "undefined" ) {
            path += `${ projectIdPath.projectId!.projectId.rawValue}/stages/${ projectIdPath.stageId!.projectStageId.rawValue }/attachments`;
        }
        else {
            path += '/' + action.projectIdPath.projectId.projectId.rawValue + '/log/';
        }
    }

    return path;
}

/**
 * redux observable epic to fetch the data from the backend, redux observable uses rxjs
 *
 * @param action$ redux observable action stream
 * @returns {*}
 */
export const fetchProjectLogEpic = (
    action$: Observable< FetchProjectLogAction >,
    store$: StateObservable< AppState >
) =>
    action$.pipe(
        ofType( FETCH_PROJECT_LOG ),
        exhaustMap( action => {
            const path = getPathForFetchProjectLog( action );

            const request = typeof action.userViewSettings === "undefined"
                ? getUserViewSettingsApiRequest( path, ProjectLogModule.identifier, store$ )
                : ({
                    path: path + getUrlParamsFromViewSettings( action.userViewSettings ),
                    userViewSettings: action.userViewSettings
                });


            return ajax( APP_CONFIG.getRequestObject( request.path ) ).pipe(
                map(response => fetchProjectLogSuccess(
                    action.projectIdPath,
                    response.response as ApiReturnType< IProjectLogEntry[] >
                ) ),
                catchError(error => {
                    return of(
                        fetchProjectLogError(action.projectIdPath, error),
                        eventNotificationError( error, store$, ProjectLogModule.identifier, "fetchProjectLog" )
                    )
                })
            )
        })
    );

/*****
 *
 * FETCH PROJECT LOG ENTRY THREAD
 *
 */

 /**
  * typescript definitions
  */
 interface FetchProjectLogEntryThreadAction extends ViewIdentifiable, ReduxAction<typeof FETCH_PROJECT_LOG_ENTRY_THREAD> {
    logId: number
    projectId: number
    logEntryId: number
 }
 interface FetchProjectLogEntryThreadSuccessAction extends ReduxAction<typeof FETCH_PROJECT_LOG_ENTRY_THREAD_SUCCESS> {
    logEntryId: number
    thread: ApiReturnType< IProjectLogEntry[] >
 }
 interface FetchProjectLogEntryThreadErrorAction extends ErrorAction<typeof FETCH_PROJECT_LOG_ENTRY_THREAD_ERROR> {
     logEntryId: number
 }

 type FetchProjectLogEntryThreadActionTypes = FetchProjectLogEntryThreadAction | FetchProjectLogEntryThreadSuccessAction | FetchProjectLogEntryThreadErrorAction

 //redux action type constants
export const FETCH_PROJECT_LOG_ENTRY_THREAD = "FETCH_PROJECT_LOG_ENTRY_THREAD";
export const FETCH_PROJECT_LOG_ENTRY_THREAD_SUCCESS = "FETCH_PROJECT_LOG_ENTRY_THREAD_SUCCESS";
export const FETCH_PROJECT_LOG_ENTRY_THREAD_ERROR = "FETCH_PROJECT_LOG_ENTRY_THREAD_ERROR";

//redux action accessor functions
export const fetchProjectLogEntryThread = ( logEntryId: number, projectId: number, logId: number, viewIdentifier: string ): FetchProjectLogEntryThreadAction =>
    ({ type: FETCH_PROJECT_LOG_ENTRY_THREAD, logEntryId, projectId, logId, viewIdentifier });
export const fetchProjectLogEntryThreadSuccess = ( logEntryId: number, thread: ApiReturnType< IProjectLogEntry[] > ): FetchProjectLogEntryThreadSuccessAction =>
    ({ type: FETCH_PROJECT_LOG_ENTRY_THREAD_SUCCESS, thread, logEntryId });
export const fetchProjectLogEntryThreadError = ( logEntryId: number, error: Error ): FetchProjectLogEntryThreadErrorAction =>
    ({ type: FETCH_PROJECT_LOG_ENTRY_THREAD_ERROR, logEntryId, error });

//redux observable epic
export const fetchProjectLogEntryThreadEpic = (
    action$: Observable< FetchProjectLogEntryThreadAction >,
    store$: StateObservable< AppState >
) =>
    action$.pipe(
        ofType( FETCH_PROJECT_LOG_ENTRY_THREAD ),
        exhaustMap( action => {
            const request = getUserViewSettingsApiRequest(
                APP_CONFIG.baseWeb +"project/"+action.projectId+"/log/"+action.logId+"/thread/"+action.logEntryId,
                action.viewIdentifier,
                store$
            )


            return ajax( APP_CONFIG.getRequestObject( request.path ) ).pipe(
                map(response => fetchProjectLogEntryThreadSuccess(
                    action.logEntryId,
                    response.response as ApiReturnType< IProjectLogEntry[] >
                )),
                catchError(error => {
                    return of(
                        fetchProjectLogEntryThreadError(action.logEntryId, error),
                        eventNotificationShow(
                            EVENT_NOTIFICATION_TYPE.customAjaxError,
                            'Daten konnten nicht abgerufen werden, Fehlercode: '
                        )
                    )
                })
            )
        })
    );

/**
 * CREATE PROJECT LOG ENTRY
 */
export interface CreateProjectLogEntryAction extends ProjectLogEntryRequestProp, FileRequestProp, ReduxAction<typeof CREATE_PROJECT_LOG_ENTRY> {
    /** uri of return to, after successful insert/update */
    uri: string
}
export interface CreateProjectLogEntrySuccessAction extends ReduxAction<typeof CREATE_PROJECT_LOG_ENTRY_SUCCESS> {}
export interface CreateProjectLogEntryErrorAction extends ErrorAction<typeof CREATE_PROJECT_LOG_ENTRY_ERROR> {}

export type CreateProjectLogEntryActionTypes = CreateProjectLogEntryAction
    | CreateProjectLogEntrySuccessAction
    | CreateProjectLogEntryErrorAction

export const CREATE_PROJECT_LOG_ENTRY = "CREATE_PROJECT_LOG_ENTRY";
export const CREATE_PROJECT_LOG_ENTRY_SUCCESS = "CREATE_PROJECT_LOG_ENTRY_SUCCESS";
export const CREATE_PROJECT_LOG_ENTRY_ERROR = "CREATE_PROJECT_LOG_ENTRY_ERROR";

export function createProjectLogEntry(
    entry: ProjectLogEntryRequest,
    files: File[] ,
    uri: string
): CreateProjectLogEntryAction {
    return { type: CREATE_PROJECT_LOG_ENTRY, projectLogEntryReq: entry, files, uri };
}
export function createProjectLogEntrySuccess(  ): CreateProjectLogEntrySuccessAction {
    return { type: CREATE_PROJECT_LOG_ENTRY_SUCCESS };
}
export function createProjectLogEntryError( error: any ): CreateProjectLogEntryErrorAction {
    return { type: CREATE_PROJECT_LOG_ENTRY_ERROR, error };
}

export function createProjectLogEntryEpic(
    action$: Observable< CreateProjectLogEntryAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( CREATE_PROJECT_LOG_ENTRY ),
        exhaustMap( action => {

            return ajax(
                APP_CONFIG.getRequestObject(
                    APP_CONFIG.baseWeb+'project/log/'+ action.projectLogEntryReq.projectLogId,
                    RouteMethods.post,
                    toFormDataEncode( 'entry', action.projectLogEntryReq, action.files ),
                    multipartHeaders()
                )
            ).pipe(
                    mergeMap( _ => of(
                        createProjectLogEntrySuccess(),
                        fetchProjectById( makeNumericId( action.projectLogEntryReq.projectId )),
                        eventNotificationSuccess(),
                        sendUserToPage(
                            action.uri,
                            ProjectLogModule.identifier
                        )

                    ) ),
                    catchError( error => of(
                        createProjectLogEntryError( error ),
                        eventNotificationError( error, store$, ProjectLogModule.identifier, "createProjectLogEntry" )
                    ))
                )
        })
    );
}

 /**
  * UPDATE PROJECT LOG ENTRY
  */
 export interface UpdateProjectLogEntryAction extends ProjectLogEntryRequestProp, ReduxAction<typeof UPDATE_PROJECT_LOG_ENTRY> {}
export interface UpdateProjectLogEntrySuccessAction extends ReduxAction<typeof UPDATE_PROJECT_LOG_ENTRY_SUCCESS> {}
export interface UpdateProjectLogEntryErrorAction extends ErrorAction<typeof UPDATE_PROJECT_LOG_ENTRY_ERROR> {}

 export type UpdateProjectLogEntryActionTypes = UpdateProjectLogEntryAction
     | UpdateProjectLogEntrySuccessAction
     | UpdateProjectLogEntryErrorAction

 export const UPDATE_PROJECT_LOG_ENTRY = "UPDATE_PROJECT_LOH_ENTRY";
 export const UPDATE_PROJECT_LOG_ENTRY_SUCCESS = "UPDATE_PROJECT_LOH_ENTRY_SUCCESS";
 export const UPDATE_PROJECT_LOG_ENTRY_ERROR = "UPDATE_PROJECT_LOH_ENTRY_ERROR";

 export function updateProjectLogEntry( entry: ProjectLogEntryRequest ): UpdateProjectLogEntryAction {
     return { type: UPDATE_PROJECT_LOG_ENTRY, projectLogEntryReq: entry };
 }
 export function updateProjectLogEntrySuccess(): UpdateProjectLogEntrySuccessAction {
     return { type: UPDATE_PROJECT_LOG_ENTRY_SUCCESS };
 }
 export function updateProjectLogEntryError( error: any ): UpdateProjectLogEntryErrorAction {
     return { type: UPDATE_PROJECT_LOG_ENTRY_ERROR, error };
 }

 export function updateProjectLogEntryEpic(
     action$: Observable< UpdateProjectLogEntryAction >,
     store$: StateObservable< AppState >
 ) {
     return action$.pipe(
         ofType( UPDATE_PROJECT_LOG_ENTRY ),
         exhaustMap( action => {
             return ajax( APP_CONFIG.getRequestObject('', RouteMethods.put, action.projectLogEntryReq ) )
                 .pipe(
                     mergeMap( _ => of(
                        updateProjectLogEntrySuccess(),
                         fetchProjectById( makeNumericId( action.projectLogEntryReq.projectId )),
                        eventNotificationSuccess(),
                        sendUserToPage( RouteConfig.dashboard.routes.main.path, ProjectLogModule.identifier )
                     ) ),
                     catchError( error => of(
                         updateProjectLogEntryError( error ),
                         eventNotificationError( error, store$, ProjectLogModule.identifier, "updateProjectLogEntry" )
                     ) )
                 )
         })
     );
 }

 /**
  * DELETE PROJECT LOG ENTRY
  */
 export interface DeleteProjectLogEntryAction extends ProjectLogEntryId, ReduxAction<typeof DELETE_PROJECT_LOG_ENTRY> {}
 export interface DeleteProjectLogEntrySuccessAction extends ReduxAction<typeof DELETE_PROJECT_LOG_ENTRY_SUCCESS> {}
 export interface DeleteProjectLogEntryErrorAction extends ErrorAction<typeof DELETE_PROJECT_LOG_ENTRY_ERROR> {}

 export type DeleteProjectLogEntryActionTypes = DeleteProjectLogEntryAction
    | DeleteProjectLogEntrySuccessAction
    | DeleteProjectLogEntryErrorAction

 export const DELETE_PROJECT_LOG_ENTRY = "DELETE_PROJECT_LOG_ENTRY";
 export const DELETE_PROJECT_LOG_ENTRY_SUCCESS = "DELETE_PROJECT_LOG_ENTRY_SUCCESS";
 export const DELETE_PROJECT_LOG_ENTRY_ERROR = "DELETE_PROJECT_LOG_ENTRY_ERROR";

export function deleteProjectLogEntry( entryId: Identifier< number> ): DeleteProjectLogEntryAction {
    return { type: DELETE_PROJECT_LOG_ENTRY, projectLogEntryId: entryId };
}
export function deleteProjectLogEntrySuccess(): DeleteProjectLogEntrySuccessAction {
    return { type: DELETE_PROJECT_LOG_ENTRY_SUCCESS };
}
export function deleteProjectLogEntryError( error: any ): DeleteProjectLogEntryErrorAction {
    return { type: DELETE_PROJECT_LOG_ENTRY_ERROR, error };
}

export function deleteProjectLogEntryEpic(
    action$: Observable< DeleteProjectLogEntryAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( DELETE_PROJECT_LOG_ENTRY ),
        exhaustMap( action => {
            return ajax( APP_CONFIG.getRequestObject( '', RouteMethods.delete ) ).pipe(
                mergeMap( _ => of(
                    deleteProjectLogEntrySuccess(),
                    eventNotificationSuccess()
                ) ),
                catchError( error => of(
                    deleteProjectLogEntryError( error ),
                    eventNotificationError( error, store$, ProjectLogModule.identifier, "deleteProjectLogEntry" )
                ) )
            )
        })
    );
}

 /**
  * RESTORE PROJECT LOG ENTRY
  */
 export interface RestoreProjectLogEntryAction extends ProjectLogEntryId, ReduxAction<typeof RESTORE_PROJECT_LOG_ENTRY> {}
 export interface RestoreProjectLogEntrySuccessAction extends ReduxAction<typeof RESTORE_PROJECT_LOG_ENTRY_SUCCESS> {}
 export interface RestoreProjectLogEntryErrorAction extends ErrorAction<typeof RESTORE_PROJECT_LOG_ENTRY_ERROR> {}

 export type RestoreProjectLogEntryActionTypes = RestoreProjectLogEntryAction
    | RestoreProjectLogEntrySuccessAction
    | RestoreProjectLogEntryErrorAction

 export const RESTORE_PROJECT_LOG_ENTRY = "RESTORE_PROJECT_LOG_ENTRY";
 export const RESTORE_PROJECT_LOG_ENTRY_SUCCESS = "RESTORE_PROJECT_LOG_ENTRY_SUCCESS";
 export const RESTORE_PROJECT_LOG_ENTRY_ERROR = "RESTORE_PROJECT_LOG_ENTRY_ERROR";

 export function restoreProjectLogEntry( entryId: Identifier<number> ): RestoreProjectLogEntryAction {
     return { type: RESTORE_PROJECT_LOG_ENTRY, projectLogEntryId: entryId };
 }
 export function restoreProjectLogEntrySuccess(): RestoreProjectLogEntrySuccessAction {
     return { type: RESTORE_PROJECT_LOG_ENTRY_SUCCESS };
 }
 export function restoreProjectLogEntryError( error: any ): RestoreProjectLogEntryErrorAction {
     return { type: RESTORE_PROJECT_LOG_ENTRY_ERROR, error };
 }

 export function restoreProjectLogEntryEpic(
     action$: Observable< RestoreProjectLogEntryAction >,
     store$: StateObservable< AppState >
 ) {
     return action$.pipe(
         ofType( RESTORE_PROJECT_LOG_ENTRY ),
         exhaustMap( action => {
             return ajax( APP_CONFIG.getRequestObject( '', RouteMethods.patch ) ).pipe(
                 mergeMap( _ => of(
                    restoreProjectLogEntrySuccess(),
                    eventNotificationSuccess()
                 ) ),
                 catchError( error => of(
                     restoreProjectLogEntryError( error ),
                     eventNotificationError( error, store$, ProjectLogModule.identifier, "restoreProjectLogEntry" )
                 ) )
             )
         } )
     );
 }

 /**
  * FETCH PROJECT LOG ENTRY ATTACHMENTS
  */


 /**
  * MARKED AN ENTRY READ / UNREAD
  */
 //typescript redux action types
 type FetchLogAfterMarkedRead = () => FetchProjectLogAction
 type FetchThreadAfterMarkedRead = () => FetchProjectLogEntryThreadAction
 type FetchAfterMarkedRead = FetchLogAfterMarkedRead | FetchThreadAfterMarkedRead

 export interface MarkProjectLogEntryReadAction extends ProjectLogId, ProjectLogEntryId, ReduxAction<typeof MARK_PROJECT_LOG_ENTRY_READ> {
    isRead: boolean
     fetchEntries: FetchAfterMarkedRead | ( () => void )
 }
 export interface MarkProjectLogEntryReadSuccessAction extends ReduxAction<typeof MARK_PROJECT_LOG_ENTRY_READ_SUCCESS> {}
 export interface MarkProjectLogEntryReadErrorAction extends ErrorAction<typeof MARK_PROJECT_LOG_ENTRY_READ_ERROR> {}

 export type MarkProjectLogEntryReadActionTypes = MarkProjectLogEntryReadAction
    | MarkProjectLogEntryReadSuccessAction
    | MarkProjectLogEntryReadErrorAction

 //redux action type constants
 export const MARK_PROJECT_LOG_ENTRY_READ = "MARK_PROJECT_LOG_ENTRY_READ";
 export const MARK_PROJECT_LOG_ENTRY_READ_SUCCESS = "MARK_PROJECT_LOG_ENTRY_READ_SUCCESS";
 export const MARK_PROJECT_LOG_ENTRY_READ_ERROR = "MARK_PROJECT_LOG_ENTRY_READ_ERROR";

 //redux action accessor functions
 export function markProjectLogEntryRead(
     projectLogId: Identifier<number>,
     projectLogEntryId: Identifier<number>,
     isRead: boolean,
     fetchEntries: FetchAfterMarkedRead | ( () => void )
 ): MarkProjectLogEntryReadAction {
     return { type: MARK_PROJECT_LOG_ENTRY_READ, projectLogEntryId, isRead, projectLogId, fetchEntries };
 }
 export function markProjectLogEntryReadSuccess(): MarkProjectLogEntryReadSuccessAction {
     return { type: MARK_PROJECT_LOG_ENTRY_READ_SUCCESS };
 }
 export function markProjectLogEntryReadError( error: any ): MarkProjectLogEntryReadErrorAction {
     return { type: MARK_PROJECT_LOG_ENTRY_READ_ERROR, error };
 }

 //redux observable epic
 export function markProjectLogEntryReadEpic(
     action$: Observable< MarkProjectLogEntryReadAction >,
     store$: StateObservable< AppState >
 ) {
     return action$.pipe(
         ofType( MARK_PROJECT_LOG_ENTRY_READ ),
         exhaustMap( action => {
             let path = `project/log/${ action.projectLogId.rawValue }/entry/${ action.projectLogEntryId.rawValue }/seen`;

             if( ! action.isRead ) {
                 path = `project/log/${ action.projectLogId.rawValue }/entry/${ action.projectLogEntryId.rawValue }/unseen`;
             }

             return ajax(
                 APP_CONFIG.getRequestObject( APP_CONFIG.baseWeb + path, RouteMethods.put )
             ).pipe(
                mergeMap( _ => of(
                    markProjectLogEntryReadSuccess(),
                    eventNotificationSuccess(),
                    action.fetchEntries()
                )),
                catchError( error => of(
                    markProjectLogEntryReadError( error ),
                    eventNotificationError( error, store$, ProjectLogModule.identifier, "markProjectLogEntryRead" )
                ) )
             )

         })
     );
 }
